import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table, Tag, Typography, Space, Button, Select, Empty, Skeleton, Tooltip,
  Card, Row, Col, Badge, Layout, message, Modal, Descriptions, Spin,
} from 'antd';
import {
  LinkOutlined, ReloadOutlined, DownloadOutlined, ToolOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
// ExcelJS is ~700 KB minified — only imported when the user clicks Export.
// Static import was forcing it into the initial bundle for every visit.
type ExcelJSModule = typeof import('exceljs');
const loadExcelJS = (): Promise<ExcelJSModule> =>
  import('exceljs').then((m) => (m as unknown as { default: ExcelJSModule }).default ?? m);
import { fetchTasksForLocation, fetchTicketCounts, fetchTaskCounts, updateTask, fetchNodeDetail } from '../config/api';
import { brandColors } from '../theme/antd-theme';
import LocationPane from '../components/LocationList/LocationPane';
import CollapsedRail from '../components/Layout/CollapsedRail';
import type { LocationWithCounts } from '../types/triage';
import { formatDurationDays } from '../utils/formatDuration';
import {
  ACTION_IDS,
  type LocationTask,
  type NodeDetailResponse,
  type NodePastTicket,
  type TaskStatus,
} from '../types/triage';
import { classTagMap, priorityColor, priorityLabel, taskStatusBadgeMap } from '../components/triageMaps';
import { useViewportWidth } from '../hooks/useViewportWidth';

dayjs.extend(relativeTime);

const DASHBOARD_URL = (import.meta.env.VITE_DASHBOARD_URL || 'https://dashboard.sensorflow.cloud').replace(/\/$/, '');

// action_id 2 = "replace node" — the action that requires bringing replacement hardware
const REPLACE_NODE_ACTION_ID = ACTION_IDS.REPLACE_NODE;

const NODE_TYPE_LABEL: Record<string, string> = {
  energy: 'Energy Node',
  energy_switchless: 'Energy Node (switchless)',
  aircon: 'Aircon Node',
  aircon_acir: 'Aircon Node',
  door: 'Door Node',
  occupancy: 'Occupancy Node',
  compressor: 'Compressor Node',
  pipe: 'Pipe Node',
};

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'done', label: 'Done' },
  { value: 'canceled', label: 'Canceled' },
  // 2026-05-25: previously omitted, so tasks already in 'auto-resolved'
  // couldn't be transitioned via the Select dropdown. STATUS_LABEL has
  // always included it; keeping these two in sync.
  { value: 'auto-resolved', label: 'Auto-resolved' },
];

const EXECUTOR_FILTERS = [
  { text: 'Contractor', value: 'Contractor' },
  { text: 'Hotel Engineering', value: 'Hotel Engineering' },
  { text: 'Project Coordinator', value: 'Project coordinator' },
];

// --- Excel export styling ---
// Brand palette
const BRAND_PRIMARY = 'FF006EB7';   // SensorFlow blue
const BRAND_DARK = 'FF034E83';      // Header row fill
const ZEBRA_FILL = 'FFF4F8FC';      // Alternating row fill
const TOTAL_FILL = 'FFE8F0FA';      // Totals row fill
const BORDER_COLOR = 'FFD9DFE8';

const thinBorder: Partial<ExcelJS.Borders> = {
  top:    { style: 'thin', color: { argb: BORDER_COLOR } },
  left:   { style: 'thin', color: { argb: BORDER_COLOR } },
  right:  { style: 'thin', color: { argb: BORDER_COLOR } },
  bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
};

type SheetCol = {
  header: string;
  key: string;
  width: number;
  align?: 'left' | 'center' | 'right';
  wrap?: boolean;
  hyperlink?: boolean;
};

// Apply title banner + styled header + data rows + optional totals row to a sheet.
function buildSheet(
  ws: ExcelJS.Worksheet,
  title: string,
  cols: SheetCol[],
  rows: Record<string, any>[],
  opts: { hasTotal?: boolean } = {},
) {
  const ncols = cols.length;
  ws.properties.defaultRowHeight = 18;

  // Row 1: Title banner (merged)
  ws.mergeCells(1, 1, 1, ncols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_PRIMARY } };
  ws.getRow(1).height = 26;

  // Row 2: thin spacer
  ws.getRow(2).height = 4;

  // Row 3: Column headers
  ws.columns = cols.map((c) => ({ header: c.header, key: c.key, width: c.width }));
  // ws.columns sets headerRow to row 1 — we override by writing headers manually at row 3:
  const headerRow = ws.getRow(3);
  cols.forEach((c, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = c.header;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_DARK } };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 22;

  // Data rows start at row 4
  const dataStart = 4;
  const totalRowIdx = opts.hasTotal ? dataStart + rows.length - 1 : -1;

  rows.forEach((rowData, rIdx) => {
    const excelRow = ws.getRow(dataStart + rIdx);
    const isTotal = dataStart + rIdx === totalRowIdx;
    const zebra = !isTotal && rIdx % 2 === 1;

    cols.forEach((c, i) => {
      const cell = excelRow.getCell(i + 1);
      const raw = rowData[c.key];

      if (c.hyperlink && typeof raw === 'string' && raw && !isTotal) {
        cell.value = { text: 'Open', hyperlink: raw };
        cell.font = { color: { argb: BRAND_PRIMARY }, underline: true, size: 11 };
      } else {
        cell.value = raw === '' || raw == null ? null : raw;
        cell.font = { size: 11, bold: isTotal };
      }

      cell.alignment = {
        vertical: 'middle',
        horizontal: c.align ?? 'left',
        wrapText: !!c.wrap,
        indent: c.align === 'left' ? 1 : 0,
      };

      if (isTotal) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_FILL } };
        cell.border = {
          ...thinBorder,
          top: { style: 'medium', color: { argb: BRAND_DARK } },
        };
      } else {
        if (zebra) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA_FILL } };
        }
        cell.border = thinBorder;
      }
    });
  });

  // Freeze the title + header rows
  ws.views = [{ state: 'frozen', ySplit: 3 }];

  // Auto-filter on the header row
  ws.autoFilter = {
    from: { row: 3, column: 1 },
    to: { row: 3, column: ncols },
  };

  // Tab color
  ws.properties.tabColor = { argb: BRAND_PRIMARY };
}

// Aggregate helpers used by both the Cover sheet and the row aggregations.
const STATUS_LABEL: Record<TaskStatus, string> = {
  'pending': 'Pending',
  'in-progress': 'In Progress',
  'accepted': 'Accepted',
  'done': 'Done',
  'canceled': 'Canceled',
  'auto-resolved': 'Auto-resolved',
};

const PRIORITY_LABEL: Record<number, string> = {
  1: 'P1 — Critical',
  2: 'P2 — High',
  3: 'P3 — Medium',
  4: 'P4 — Low',
};

function fmtPriority(p: number | null | undefined): string {
  if (p == null) return '—';
  return PRIORITY_LABEL[p] ?? `P${p}`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '';
  return dayjs(s).format('YYYY-MM-DD');
}

// --- Excel export ---
async function exportExcel(tasks: LocationTask[], locationName: string) {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Fleet Triage';
  wb.created = new Date();

  const dateStr = dayjs().format('MMM D, YYYY');

  // ---------- Sheet 1: Cover / Summary ----------
  // One-glance overview — what's in this packet, how much work, by when.
  // Field tech reads this first, then jumps to Work Tasks. Client/PM reads
  // this to plan room access (skips technical sheets).
  const totalExternal = tasks.reduce(
    (s, t) => s + (t.action_time_allocated_external_mins ?? 0),
    0,
  );
  const totalInternal = tasks.reduce(
    (s, t) => s + (t.action_time_allocated_internal_mins ?? 0),
    0,
  );
  const uniqueRooms = new Set(tasks.map((t) => t.key_id || t.key_name || t.autoset_name).filter(Boolean)).size;
  const uniqueNodes = new Set(tasks.map((t) => t.observable_id).filter(Boolean)).size;
  const priorityBuckets: Record<string, number> = {};
  for (const t of tasks) {
    const k = fmtPriority(t.priority);
    priorityBuckets[k] = (priorityBuckets[k] ?? 0) + 1;
  }
  const statusBuckets: Record<string, number> = {};
  for (const t of tasks) {
    const k = STATUS_LABEL[t.status] ?? t.status;
    statusBuckets[k] = (statusBuckets[k] ?? 0) + 1;
  }
  const earliestDue = tasks
    .map((t) => t.due_date)
    .filter((d): d is string => !!d)
    .sort()[0] ?? null;
  const hours = (mins: number) => (mins / 60).toFixed(1);

  const coverRows: Record<string, any>[] = [
    { field: 'Location',           value: locationName },
    { field: 'Generated',          value: dayjs().format('YYYY-MM-DD HH:mm') },
    { field: 'Total tasks',        value: tasks.length },
    { field: 'Unique rooms',       value: uniqueRooms },
    { field: 'Unique nodes',       value: uniqueNodes },
    { field: 'Est. on-site time (h)',  value: `${hours(totalExternal)} (${totalExternal} min)` },
    { field: 'Est. internal time (h)', value: `${hours(totalInternal)} (${totalInternal} min)` },
    { field: 'Earliest due date',  value: earliestDue ? fmtDate(earliestDue) : 'no dates set' },
    { field: '', value: '' },
    { field: 'BY PRIORITY', value: '' },
    ...Object.entries(priorityBuckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => ({ field: k, value: v })),
    { field: '', value: '' },
    { field: 'BY STATUS', value: '' },
    ...Object.entries(statusBuckets)
      .sort(([, a], [, b]) => b - a)
      .map(([k, v]) => ({ field: k, value: v })),
  ];
  const coverCols: SheetCol[] = [
    { header: 'Field', key: 'field', width: 30 },
    { header: 'Value', key: 'value', width: 50 },
  ];
  const coverWs = wb.addWorksheet('Summary');
  buildSheet(coverWs, `Work Packet — ${locationName} — ${dateStr}`, coverCols, coverRows);

  // ---------- Sheet 2: Work Tasks (field-tech, sorted by priority) ----------
  // Priority first so a tech working through a paper printout hits the
  // critical rooms before the low-priority ones. Title + Room + MAC together
  // identify the exact unit in the field.
  const taskCols: SheetCol[] = [
    { header: 'Pri',           key: 'priority',     width: 12, align: 'center' },
    { header: 'Done ✓',        key: 'done',         width: 8,  align: 'center' },
    { header: 'Room / Key',    key: 'key',          width: 24 },
    { header: 'Task',          key: 'title',        width: 44, wrap: true },
    { header: 'Action',        key: 'action',       width: 22 },
    { header: 'Node MAC',      key: 'mac',          width: 18 },
    { header: 'Node Type',     key: 'nodeType',     width: 18 },
    { header: 'Status',        key: 'status',       width: 12, align: 'center' },
    { header: 'Assigned',      key: 'assigned',     width: 16 },
    { header: 'Due',           key: 'due',          width: 12, align: 'center' },
    { header: 'Internal (min)', key: 'internal',    width: 12, align: 'right' },
    { header: 'External (min)', key: 'external',    width: 12, align: 'right' },
    { header: 'Equipment',     key: 'equipment',    width: 30, wrap: true },
    { header: 'Instructions',  key: 'instructions', width: 12, align: 'center', hyperlink: true },
    { header: 'Notes',         key: 'notes',        width: 32, wrap: true },
  ];
  const sortedTasks = [...tasks].sort((a, b) => {
    const pa = a.priority ?? 99;
    const pb = b.priority ?? 99;
    if (pa !== pb) return pa - pb;
    const ka = (a.key_name || a.autoset_name || '').toLowerCase();
    const kb = (b.key_name || b.autoset_name || '').toLowerCase();
    return ka.localeCompare(kb);
  });
  const taskRows = sortedTasks.map((t) => ({
    priority: fmtPriority(t.priority),
    done: '',
    key: t.key_name || t.autoset_name || '',
    title: t.title || '',
    action: t.action_name || '',
    mac: t.observable_id,
    nodeType: NODE_TYPE_LABEL[t.observable_info || ''] || t.observable_info || '',
    status: STATUS_LABEL[t.status] ?? t.status,
    assigned: t.executor || t.claimer_email || '',
    due: fmtDate(t.due_date),
    internal: t.action_time_allocated_internal_mins ?? null,
    external: t.action_time_allocated_external_mins ?? null,
    equipment: (t.action_equipment ?? []).join(', '),
    instructions: t.action_instruction_link || '',
    notes: t.description || '',
  }));
  const taskWs = wb.addWorksheet('Work Tasks');
  buildSheet(taskWs, `Work Tasks — ${locationName} — ${dateStr}`, taskCols, taskRows);

  // ---------- Sheet 3: Room Access (client-facing) ----------
  // Groups tasks by key/room so clients can plan unit access. Highest priority
  // surfaces first so the client/PM can flag critical rooms. Includes node
  // count, internal (ops) and external (client) time estimates.
  type RoomAgg = {
    label: string;
    taskCount: number;
    nodes: Set<string>;
    actions: Set<string>;
    topPriority: number;
    totalInternal: number;
    totalExternal: number;
  };
  const byRoom = new Map<string, RoomAgg>();
  for (const t of tasks) {
    const label = t.key_name || t.autoset_name || '(unassigned)';
    const groupKey = t.key_id || label;
    const prev = byRoom.get(groupKey) ?? {
      label,
      taskCount: 0,
      nodes: new Set<string>(),
      actions: new Set<string>(),
      topPriority: 99,
      totalInternal: 0,
      totalExternal: 0,
    };
    prev.taskCount += 1;
    if (t.observable_id) prev.nodes.add(t.observable_id);
    if (t.action_name) prev.actions.add(t.action_name);
    if (t.priority != null && t.priority < prev.topPriority) prev.topPriority = t.priority;
    if (t.action_time_allocated_internal_mins != null) {
      prev.totalInternal += t.action_time_allocated_internal_mins;
    }
    if (t.action_time_allocated_external_mins != null) {
      prev.totalExternal += t.action_time_allocated_external_mins;
    }
    byRoom.set(groupKey, prev);
  }
  if (byRoom.size > 0) {
    const roomCols: SheetCol[] = [
      { header: 'Top Pri',         key: 'priority', width: 10, align: 'center' },
      { header: 'Room / Key',      key: 'room',     width: 28 },
      { header: '# Nodes',         key: 'nodes',    width: 10, align: 'right' },
      { header: '# Tasks',         key: 'tasks',    width: 10, align: 'right' },
      { header: 'Actions',         key: 'actions',  width: 40, wrap: true },
      { header: 'Internal (min)',  key: 'internal', width: 14, align: 'right' },
      { header: 'On-site (min)',   key: 'external', width: 14, align: 'right' },
    ];
    const roomAggs = [...byRoom.values()].sort(
      (a, b) =>
        a.topPriority - b.topPriority ||
        b.totalExternal - a.totalExternal ||
        b.taskCount - a.taskCount ||
        a.label.localeCompare(b.label),
    );
    const roomRows: Record<string, any>[] = roomAggs.map((r) => ({
      priority: r.topPriority < 99 ? `P${r.topPriority}` : '—',
      room: r.label,
      nodes: r.nodes.size,
      tasks: r.taskCount,
      actions: [...r.actions].sort().join(', '),
      internal: r.totalInternal || null,
      external: r.totalExternal || null,
    }));
    const totalTasks = roomAggs.reduce((s, r) => s + r.taskCount, 0);
    const totalNodes = roomAggs.reduce((s, r) => s + r.nodes.size, 0);
    const totalRoomInternal = roomAggs.reduce((s, r) => s + r.totalInternal, 0);
    const totalRoomExternal = roomAggs.reduce((s, r) => s + r.totalExternal, 0);
    roomRows.push({
      priority: '',
      room: `TOTAL (${byRoom.size} rooms)`,
      nodes: totalNodes,
      tasks: totalTasks,
      actions: '',
      internal: totalRoomInternal || null,
      external: totalRoomExternal || null,
    });
    const roomWs = wb.addWorksheet('Room Access');
    buildSheet(
      roomWs,
      `Room Access — ${locationName} — ${dateStr}`,
      roomCols,
      roomRows,
      { hasTotal: true },
    );
  }

  // ---------- Sheet 3: Action Summary ----------
  type ActionAgg = {
    name: string;
    count: number;
    internal: number | null;
    external: number | null;
    equipment: string;
    instructions: string;
  };
  const byAction = new Map<string, ActionAgg>();
  for (const t of tasks) {
    const name = t.action_name || '(unassigned)';
    const prev = byAction.get(name);
    if (prev) {
      prev.count += 1;
    } else {
      byAction.set(name, {
        name,
        count: 1,
        internal: t.action_time_allocated_internal_mins,
        external: t.action_time_allocated_external_mins,
        equipment: (t.action_equipment ?? []).join(', '),
        instructions: t.action_instruction_link || '',
      });
    }
  }
  const actionAggs = [...byAction.values()].sort(
    (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  );

  if (actionAggs.length > 0) {
    const actionCols: SheetCol[] = [
      { header: 'Action',           key: 'action',       width: 26 },
      { header: 'Count',            key: 'count',        width: 10, align: 'right' },
      { header: 'Internal/ea (min)', key: 'intEa',       width: 16, align: 'right' },
      { header: 'External/ea (min)', key: 'extEa',       width: 16, align: 'right' },
      { header: 'Total Internal (min)', key: 'totInt',   width: 18, align: 'right' },
      { header: 'Total External (min)', key: 'totExt',   width: 18, align: 'right' },
      { header: 'Equipment',        key: 'equipment',    width: 32, wrap: true },
      { header: 'Instructions',     key: 'instructions', width: 14, align: 'center', hyperlink: true },
    ];
    const actionRows = actionAggs.map((a) => ({
      action: a.name,
      count: a.count,
      intEa: a.internal ?? null,
      extEa: a.external ?? null,
      totInt: a.internal != null ? a.internal * a.count : null,
      totExt: a.external != null ? a.external * a.count : null,
      equipment: a.equipment,
      instructions: a.instructions,
    }));
    const totInternal = actionAggs.reduce(
      (s, a) => s + (a.internal != null ? a.internal * a.count : 0),
      0,
    );
    const totExternal = actionAggs.reduce(
      (s, a) => s + (a.external != null ? a.external * a.count : 0),
      0,
    );
    actionRows.push({
      action: 'TOTAL',
      count: tasks.length,
      intEa: null,
      extEa: null,
      totInt: totInternal || null,
      totExt: totExternal || null,
      equipment: '',
      instructions: '',
    });
    const actionWs = wb.addWorksheet('Action Summary');
    buildSheet(
      actionWs,
      `Action Summary — ${locationName} — ${dateStr}`,
      actionCols,
      actionRows,
      { hasTotal: true },
    );
  }

  // ---------- Sheet 5: Nodes Needed (procurement / spare-pulling) ----------
  // What hardware to bring on-site. Counts REPLACE_NODE actions by node type,
  // suggests a 20% spare buffer (rounded up, min +1) so the tech doesn't run
  // out mid-visit, and lists the MACs being replaced so the warehouse can
  // match against existing inventory.
  type NodeAgg = {
    type: string;
    qty: number;
    macs: string[];
  };
  const aggsByType = new Map<string, NodeAgg>();
  for (const t of tasks) {
    if (t.action_id !== REPLACE_NODE_ACTION_ID) continue;
    const type = t.observable_info || 'unknown';
    const prev = aggsByType.get(type) ?? { type, qty: 0, macs: [] };
    prev.qty += 1;
    if (t.observable_id) prev.macs.push(t.observable_id);
    aggsByType.set(type, prev);
  }
  const nodeAggs = [...aggsByType.values()].sort((a, b) => b.qty - a.qty);
  if (nodeAggs.length > 0) {
    const summaryRows: Record<string, any>[] = nodeAggs.map((a) => {
      // 20% buffer rounded up, minimum +1 spare per type. Without a buffer,
      // a single dead-on-arrival unit forces the tech to make a return trip.
      const suggested = a.qty + Math.max(1, Math.ceil(a.qty * 0.2));
      return {
        nodeType: NODE_TYPE_LABEL[a.type] || a.type,
        qty: a.qty,
        suggested,
        macs: a.macs.join(', '),
      };
    });
    const total = nodeAggs.reduce((s, a) => s + a.qty, 0);
    const totalSuggested = nodeAggs.reduce(
      (s, a) => s + a.qty + Math.max(1, Math.ceil(a.qty * 0.2)),
      0,
    );
    summaryRows.push({
      nodeType: 'TOTAL',
      qty: total,
      suggested: totalSuggested,
      macs: '',
    });
    const summaryCols: SheetCol[] = [
      { header: 'Node Type',         key: 'nodeType',  width: 28 },
      { header: 'Replacing',         key: 'qty',       width: 12, align: 'right' },
      { header: 'Bring (+20% buf)',  key: 'suggested', width: 16, align: 'right' },
      { header: 'MACs being replaced', key: 'macs',    width: 60, wrap: true },
    ];
    const summaryWs = wb.addWorksheet('Nodes Needed');
    buildSheet(
      summaryWs,
      `Nodes Needed — ${locationName} — ${dateStr}`,
      summaryCols,
      summaryRows,
      { hasTotal: true },
    );
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  // UX-045: aggressive sanitize so filenames are safe on Windows (which
  // rejects slashes, colons, etc.). Previous `.replace(/\s+/g, '-')` left
  // `()`, `/`, `&` etc. intact — "Marriott (KL) / DC" produced an illegal
  // filename. Collapse anything non-alphanumeric to a single hyphen.
  const safeLoc = locationName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'location';
  a.download = `work-tasks-${safeLoc}-${dayjs().format('YYYY-MM-DD')}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

interface LocationOption {
  location_id: string;
  location_name: string;
}

const WorkTasksPage: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  // Persist the status filter in the URL so refresh / shared links keep
  // the same scope. Default 'active' stays implicit (no ?status= param).
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'active';
  const setStatusFilter = (val: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === 'active') next.delete('status');
      else next.set('status', val);
      return next;
    }, { replace: true });
  };
  const [tasks, setTasks] = useState<LocationTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [taskCountMap, setTaskCountMap] = useState<Record<string, number>>({});
  const [exporting, setExporting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // --- Ticket detail modal ---
  const [modalTask, setModalTask] = useState<LocationTask | null>(null);
  const [nodeDetailData, setNodeDetailData] = useState<NodeDetailResponse | null>(null);
  const [nodeDetailLoading, setNodeDetailLoading] = useState(false);

  const openTicketModal = useCallback(async (task: LocationTask) => {
    setModalTask(task);
    setNodeDetailData(null);
    setNodeDetailLoading(true);
    try {
      const detail = await fetchNodeDetail(task.observable_id);
      setNodeDetailData(detail);
    } catch {
      // Node detail is supplementary — modal still works without it
    } finally {
      setNodeDetailLoading(false);
    }
  }, []);

  // Load location list and task counts
  useEffect(() => {
    (async () => {
      try {
        const [ticketCounts, tCounts] = await Promise.all([
          fetchTicketCounts(),
          fetchTaskCounts(),
        ]);
        const seen = new Map<string, string>();
        for (const row of ticketCounts) {
          if (!seen.has(row.location_id)) seen.set(row.location_id, row.location_name);
        }
        const countMap: Record<string, number> = {};
        for (const row of tCounts) countMap[row.location_id] = row.count;
        setTaskCountMap(countMap);
        setLocations(
          [...seen.entries()]
            .map(([location_id, location_name]) => ({ location_id, location_name }))
            .sort((a, b) => (countMap[b.location_id] ?? 0) - (countMap[a.location_id] ?? 0)
              || a.location_name.localeCompare(b.location_name))
        );
      } catch {
        messageApi.error('Failed to load locations');
      } finally {
        setLocationsLoading(false);
      }
    })();
  }, [messageApi]);

  // Route the Reload button through this counter so the effect below (with
  // its stale-response guard) re-fires. Previously Reload called `loadTasks`
  // directly which had no `stale` flag — quick location-switch + Reload could
  // race an old response into the new location's state.
  const [reloadCounter, setReloadCounter] = useState(0);
  const loadTasks = useCallback(() => {
    if (!locationId) return;
    setReloadCounter((c) => c + 1);
  }, [locationId]);

  // Fetch tasks when locationId or the manual reload counter changes,
  // discarding stale responses to avoid a race condition where a slow
  // response from the previous location overwrites the current state.
  useEffect(() => {
    if (!locationId) return;
    let stale = false;
    setLoading(true);
    fetchTasksForLocation(locationId)
      .then((data) => { if (!stale) setTasks(data); })
      .catch(() => { if (!stale) messageApi.error('Failed to load tasks'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [locationId, reloadCounter, messageApi]);

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      const updated = await updateTask(taskId, { status: newStatus });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...updated } : t)));
    } catch {
      messageApi.error('Failed to update task status');
    }
  };

  // Prefer the location list (loaded eagerly) over the tasks list (loaded
  // per-location) so the heading is stable during initial fetch. Previously
  // it flashed the raw UUID until the first task arrived.
  const locationName = useMemo(() => {
    if (tasks.length > 0) return tasks[0].location_name;
    const fromList = locations.find((l) => l.location_id === locationId);
    return fromList?.location_name ?? locationId ?? '';
  }, [tasks, locations, locationId]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return tasks;
    if (statusFilter === 'active') return tasks.filter((t) => !['done', 'canceled', 'auto-resolved'].includes(t.status));
    return tasks.filter((t) => t.status === statusFilter);
  }, [tasks, statusFilter]);

  // Action breakdown for summary (respects filter)
  const actionBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    for (const t of filtered) {
      const name = t.action_name || 'No action';
      map[name] = (map[name] || 0) + 1;
    }
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [filtered]);

  // Materials summary: count by node type for replacement action (respects filter)
  const replacementCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    let total = 0;
    for (const t of filtered) {
      if (t.action_id === REPLACE_NODE_ACTION_ID) {
        const type = t.observable_info || 'unknown';
        counts[type] = (counts[type] || 0) + 1;
        total++;
      }
    }
    return { counts, total };
  }, [filtered]);

  const columns: ColumnsType<LocationTask> = [
    {
      title: 'Key / Room',
      key: 'key',
      width: 160,
      render: (_: unknown, record: LocationTask) => {
        const label = record.key_name || record.autoset_name || '—';
        if (record.key_id) {
          return (
            <a
              href={`${DASHBOARD_URL}/locations/${locationId}/keys/${record.key_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Space size={4}>
                <LinkOutlined style={{ fontSize: 11 }} />
                <Typography.Text style={{ fontSize: 12 }}>{label}</Typography.Text>
              </Space>
            </a>
          );
        }
        return <Typography.Text style={{ fontSize: 12 }}>{label}</Typography.Text>;
      },
    },
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      width: 240,
      ellipsis: true,
      render: (title: string, record: LocationTask) => (
        <Typography.Link
          strong
          style={{ fontSize: 13 }}
          onClick={(e) => { e.stopPropagation(); openTicketModal(record); }}
        >
          {title}
        </Typography.Link>
      ),
    },
    {
      title: 'Node Type',
      key: 'node_type',
      width: 130,
      filters: Object.entries(NODE_TYPE_LABEL).map(([k, v]) => ({ text: v, value: k })),
      onFilter: (value, record) => record.observable_info === value,
      render: (_: unknown, record: LocationTask) =>
        NODE_TYPE_LABEL[record.observable_info || ''] || record.observable_info || '—',
    },
    {
      title: 'Node MAC',
      dataIndex: 'observable_id',
      key: 'observable_id',
      width: 160,
      render: (mac: string) => (
        <Typography.Text copyable style={{ fontSize: 11, fontFamily: 'monospace' }}>{mac}</Typography.Text>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 110,
      sorter: (a, b) => {
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return dayjs(a.due_date).unix() - dayjs(b.due_date).unix();
      },
      render: (val: string | null) => {
        if (!val) return <Typography.Text type="secondary">—</Typography.Text>;
        const d = dayjs(val);
        // UX-033: three-tier overdue indicator. Binary danger/default
        // collapsed "due tomorrow" and "due in 6 months" into the same
        // visual — ops couldn't prioritize at a glance.
        const daysUntil = d.diff(dayjs(), 'day');
        let tone: 'danger' | 'warning' | 'secondary' = 'secondary';
        if (daysUntil < 0) tone = 'danger';
        else if (daysUntil <= 7) tone = 'warning';
        return (
          <Tooltip title={d.format('YYYY-MM-DD')}>
            <Typography.Text type={tone} style={{ fontSize: 12 }}>
              {d.fromNow()}
            </Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: TaskStatus, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 120 }}
          onChange={(val) => handleStatusChange(record.id, val)}
          options={STATUS_OPTIONS}
          optionRender={(opt) => (
            <Tag
              color={taskStatusBadgeMap[opt.value as string]?.color}
              style={{ margin: 0 }}
            >
              {opt.label}
            </Tag>
          )}
        />
      ),
    },
    {
      title: 'Executor',
      dataIndex: 'executor',
      key: 'executor',
      width: 130,
      filters: EXECUTOR_FILTERS,
      onFilter: (value, record) => record.executor === value,
      render: (val: string | null) => val || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Ticket Class',
      dataIndex: 'ticket_class',
      key: 'ticket_class',
      width: 110,
      ellipsis: true,
      // UX-031 / UX-XC1: use the shared classTagMap so the class chip color
      // matches the rest of the app. Previously every class rendered as the
      // default grey pill, regardless of severity.
      render: (val: string, record) => {
        const meta = classTagMap[record.ticket_class_id];
        return (
          <Tag color={meta?.color} style={{ fontSize: 11 }}>
            {val}
          </Tag>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 90,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (val: string) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(val).fromNow()}
        </Typography.Text>
      ),
    },
  ];

  // --- Location sidebar ---
  // Shape locations as LocationWithCounts so the shared LocationPane can
  // render. /tasks mode doesn't need ticket/issue rollups — totalFor() reads
  // taskCounts directly — so we stub the per-location ticketCounts.
  const locationsWithCounts = useMemo<LocationWithCounts[]>(
    () => locations.map((l) => ({
      location_id: l.location_id,
      location_name: l.location_name,
      ticketCounts: { total: taskCountMap[l.location_id] ?? 0, byClass: {} },
    })),
    [locations, taskCountMap],
  );

  // Collapsible on narrow viewports — the 220px sidebar previously had no
  // way to hide, eating a fifth of the content width on a 1024px screen.
  const SIDEBAR_COLLAPSE_BREAKPOINT = 1050;
  const viewportWidth = useViewportWidth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < SIDEBAR_COLLAPSE_BREAKPOINT);
  const collapseBandRef = useRef(window.innerWidth < SIDEBAR_COLLAPSE_BREAKPOINT);
  useEffect(() => {
    const band = viewportWidth < SIDEBAR_COLLAPSE_BREAKPOINT;
    if (band !== collapseBandRef.current) setSidebarCollapsed(band);
    collapseBandRef.current = band;
  }, [viewportWidth]);

  const sidebar = sidebarCollapsed ? (
    <CollapsedRail label="Locations" onExpand={() => setSidebarCollapsed(false)} />
  ) : (
    <Layout.Sider
      width={220}
      style={{ background: '#fff', borderRight: '1px solid #f0f0f0', overflow: 'hidden', height: '100%' }}
      trigger={null}
    >
      <LocationPane
        locations={locationsWithCounts}
        loading={locationsLoading}
        mode="tasks"
        selectedLocationId={locationId ?? null}
        onSelectLocation={(id) => navigate(`/tasks/${id}`)}
        taskCounts={taskCountMap}
        onCollapse={() => setSidebarCollapsed(true)}
      />
    </Layout.Sider>
  );

  // --- Main content ---
  const mainContent = !locationId ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#8c8c8c' }}>
      <Empty description="Select a location to view work tasks" />
    </div>
  ) : loading ? (
    <div style={{ padding: 24 }}>
      <Skeleton active paragraph={{ rows: 12 }} />
    </div>
  ) : (
    <div style={{ padding: '12px 16px', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>{locationName}</Typography.Title>
          <Tag>{filtered.length} task{filtered.length !== 1 ? 's' : ''}</Tag>
        </Space>
        <Space>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 130 }}
            size="small"
            options={[
              { label: 'Active', value: 'active' },
              { label: 'All', value: 'all' },
              { label: 'Pending', value: 'pending' },
              { label: 'In Progress', value: 'in-progress' },
              { label: 'Accepted', value: 'accepted' },
              { label: 'Done', value: 'done' },
              { label: 'Canceled', value: 'canceled' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            size="small"
            onClick={loadTasks}
            aria-label="Reload tasks"
          />
          {filtered.length > 0 && (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              loading={exporting}
              // UX-046: Wrap with try/catch + loading state. exportExcel lazy-
              // loads ExcelJS (~700KB) on first click, so first export is a
              // 1-3s silent freeze without feedback. Show spinner + surface
              // failures via toast instead of unhandled promise rejection.
              onClick={async () => {
                setExporting(true);
                try {
                  await exportExcel(filtered, locationName);
                } catch (err: any) {
                  messageApi.error(`Export failed: ${err?.message || 'unknown'}`);
                } finally {
                  setExporting(false);
                }
              }}
            >
              Export Report
            </Button>
          )}
        </Space>
      </div>

      {/* Summary strip */}
      {filtered.length > 0 && (
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col flex="1">
            <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Actions
                </Typography.Text>
                {actionBreakdown.map(([name, count]) => (
                  <Tag key={name} style={{ fontSize: 11, margin: 0 }}>
                    {name} <strong>{count}</strong>
                  </Tag>
                ))}
              </div>
            </Card>
          </Col>
          {replacementCounts.total > 0 && (
            <Col flex="none">
              <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ToolOutlined style={{ color: '#8c8c8c', fontSize: 13 }} />
                  {Object.entries(replacementCounts.counts)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => (
                      <span key={type} style={{ fontSize: 12 }}>
                        {NODE_TYPE_LABEL[type] || type}: <strong>{count}</strong>
                      </span>
                    ))}
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#006EB7' }}>
                    Total: {replacementCounts.total}
                  </span>
                </div>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <Empty description="No tasks for this location" style={{ marginTop: 60 }} />
      ) : (
        <Table<LocationTask>
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `${total} tasks` }}
          scroll={{ x: 1270 }}
          sticky
        />
      )}
    </div>
  );

  return (
    // hasSider forced: antd's Layout only applies flex-direction:row when a
    // real <Sider> child is present. `sidebar` swaps to a plain collapsed-rail
    // <div> at narrow widths, and once collapsed no <Sider> remains — Layout
    // falls back to its column default, stacking the rail above the content
    // full-width instead of side by side.
    <Layout hasSider style={{ height: '100%' }}>
      {contextHolder}
      {sidebar}
      <Layout.Content style={{ overflow: 'hidden' }}>
        {mainContent}
      </Layout.Content>

      {/* Task detail modal */}
      <Modal
        open={!!modalTask}
        onCancel={() => setModalTask(null)}
        footer={null}
        width={640}
        destroyOnClose
        title={
          modalTask ? (
            <Space>
              <InfoCircleOutlined />
              <span>Task — ticket #{modalTask.ticket_id}</span>
              <Tag>{modalTask.ticket_class}</Tag>
            </Space>
          ) : null
        }
      >
        {modalTask && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Task info */}
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="Task" span={2}>
                <Typography.Text strong>{modalTask.title}</Typography.Text>
              </Descriptions.Item>
              {modalTask.description && (
                <Descriptions.Item label="Description" span={2}>
                  {modalTask.description}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Tag color={taskStatusBadgeMap[modalTask.status]?.color}>
                  {taskStatusBadgeMap[modalTask.status]?.label ?? modalTask.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Executor">
                {modalTask.executor || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                {modalTask.action_name ? <Tag>{modalTask.action_name}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Priority">
                {/* UX-032: visible P{n} prefix + colored dot + aria-label.
                    Plain-text "High/Medium/Low" lost the standard priority
                    convention used in AgentPredictionCard. */}
                <span
                  role="img"
                  aria-label={priorityLabel[modalTask.priority] || `Priority ${modalTask.priority}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      width: 10,
                      height: 10,
                      borderRadius: 5,
                      backgroundColor: priorityColor[modalTask.priority] || '#8c8c8c',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>
                    P{modalTask.priority} ·{' '}
                    {modalTask.priority === 1
                      ? 'Critical'
                      : modalTask.priority === 2
                      ? 'High'
                      : modalTask.priority === 3
                      ? 'Medium'
                      : 'Low'}
                  </span>
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Due Date">
                {modalTask.due_date ? dayjs(modalTask.due_date).format('MMM D, YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {dayjs(modalTask.created_at).format('MMM D, YYYY')}
              </Descriptions.Item>
            </Descriptions>

            {/* Node info */}
            <Descriptions size="small" column={2} bordered title="Node Details">
              <Descriptions.Item label="Node MAC" span={2}>
                <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {modalTask.observable_id}
                </Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Node Type">
                {NODE_TYPE_LABEL[modalTask.observable_info || ''] || modalTask.observable_info || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Position">
                {modalTask.position_type || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Key / Room">
                {modalTask.key_id ? (
                  <a
                    href={`${DASHBOARD_URL}/locations/${locationId}/keys/${modalTask.key_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Space size={4}>
                      <LinkOutlined style={{ fontSize: 11 }} />
                      {modalTask.key_name || modalTask.autoset_name || '—'}
                    </Space>
                  </a>
                ) : (
                  modalTask.key_name || modalTask.autoset_name || '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                {modalTask.location_name}
              </Descriptions.Item>
            </Descriptions>

            {/* Additional node detail (fetched async) */}
            {nodeDetailLoading ? (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Spin size="small" />
                <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  Loading node details...
                </Typography.Text>
              </div>
            ) : nodeDetailData && (
              <Descriptions size="small" column={2} bordered title="Node Health">
                {nodeDetailData.batteryStatus && (
                  <>
                    <Descriptions.Item label="Battery">
                      {/* avg_battery_lvl can be null even when batteryStatus
                          object is present (older nodes without a measurement
                          window). Math.round(null) → 0 quietly; Math.round
                          of undefined → NaN rendered as "NaN%". */}
                      {typeof nodeDetailData.batteryStatus.avg_battery_lvl === 'number'
                        ? `${Math.round(nodeDetailData.batteryStatus.avg_battery_lvl)}%`
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Est. Life Left">
                      {nodeDetailData.batteryStatus.months_of_life_left != null
                        ? `${nodeDetailData.batteryStatus.months_of_life_left} months`
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Battery Age">
                      {formatDurationDays(nodeDetailData.batteryStatus.current_age)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Battery Type">
                      {nodeDetailData.batteryStatus.battery_type || '—'}
                    </Descriptions.Item>
                  </>
                )}
                {nodeDetailData.gateway && (
                  <Descriptions.Item label="Gateway" span={2}>
                    <Space>
                      {nodeDetailData.gateway.balena_device_name || nodeDetailData.gateway.balena_id}
                      <Badge
                        status={nodeDetailData.gateway.is_online ? 'success' : 'error'}
                        text={nodeDetailData.gateway.is_online ? 'Online' : 'Offline'}
                      />
                    </Space>
                  </Descriptions.Item>
                )}
                {nodeDetailData.pastTickets && nodeDetailData.pastTickets.length > 0 && (
                  <Descriptions.Item label="Past Tickets" span={2}>
                    <Space wrap>
                      {nodeDetailData.pastTickets.slice(0, 5).map((pt: NodePastTicket, i: number) => (
                        <Tooltip key={i} title={`${pt.status_name || ''} — ${dayjs(pt.ticket_created).format('MMM D, YYYY')}`}>
                          <Tag style={{ fontSize: 11, margin: 0 }}>
                            {pt.ticket_class || `#${pt.ticket_id}`}
                          </Tag>
                        </Tooltip>
                      ))}
                      {nodeDetailData.pastTickets.length > 5 && (
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          +{nodeDetailData.pastTickets.length - 5} more
                        </Typography.Text>
                      )}
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            )}
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default WorkTasksPage;
